import React, { useEffect, useRef, useState } from "react";
import { FlowResultAttachment, FlowResults } from "../../../models/flowResults";

export const formatFlowValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "")
    return "Not recorded";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

// Existing WAFlow result paths historically rendered missing values as blank.
// Keep that behavior separate from the richer structured-result presentation.
const formatLegacyFlowValue = (value: unknown): string => {
  if (value === null || value === undefined || typeof value === "boolean")
    return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const ResultAttachment = ({
  attachment,
  loadAttachment,
}: {
  attachment: FlowResultAttachment;
  loadAttachment: (key: string) => Promise<Blob>;
}) => {
  const [url, setUrl] = useState<string>();
  const [mime, setMime] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const objectUrl = useRef<string>();
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    };
  }, []);

  const preview = async () => {
    setLoading(true);
    setError(false);
    try {
      const blob = await loadAttachment(attachment.key);
      if (!mounted.current) return;
      const type = blob.type.split(";")[0].toLowerCase();
      if (
        !blob.size ||
        !["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(
          type
        )
      ) {
        throw new Error("Unsupported attachment");
      }
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = URL.createObjectURL(blob);
      setUrl(objectUrl.current);
      setMime(type);
    } catch {
      if (mounted.current) setError(true);
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  return (
    <section className="mt-4" data-html2canvas-ignore>
      <h3 className="title is-6">{attachment.title}</h3>
      <p>{attachment.name || "Attached file"}</p>
      {!attachment.available ? (
        <p>This file is unavailable or not cleared for viewing.</p>
      ) : (
        <>
          {!url && (
            <button
              type="button"
              className="button is-small is-link is-light"
              onClick={preview}
              disabled={loading}
            >
              {loading
                ? "Loading file…"
                : error
                ? "Retry preview"
                : "Preview file"}
            </button>
          )}
          {error && (
            <p role="alert">Could not load the file. Please try again.</p>
          )}
          {url && (
            <>
              <p>
                <a href={url} download={attachment.name || "attachment"}>
                  Download file
                </a>
              </p>
              {mime === "application/pdf" ? (
                <iframe
                  title={`${attachment.title} PDF preview`}
                  src={url}
                  style={{ width: "100%", height: 440, border: 0 }}
                />
              ) : (
                <img
                  alt={attachment.title}
                  src={url}
                  style={{
                    maxWidth: "100%",
                    maxHeight: 440,
                    objectFit: "contain",
                  }}
                />
              )}
            </>
          )}
        </>
      )}
    </section>
  );
};

export const FlowResultsContent = ({
  data,
  response,
  loadAttachment,
}: {
  data?: FlowResults;
  response?: Record<string, any>;
  loadAttachment: (key: string) => Promise<Blob>;
}) => {
  const display = data?.display;
  if (display) {
    return (
      <>
        {display.notice && (
          <p className="notification is-warning is-light" role="status">
            {display.notice}
          </p>
        )}
        {display.sections.map((section, index) => (
          <section key={index} className="mb-4">
            <h3 className="title is-6 mb-2">{section.title}</h3>
            <dl
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "12px 20px",
              }}
            >
              {section.fields.map((field, fieldIndex) => (
                <div key={fieldIndex} className="mb-2">
                  <dt className="has-text-weight-semibold">{field.label}</dt>
                  <dd
                    style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                  >
                    {formatFlowValue(field.value)}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
        {display.tables?.map((table, tableIndex) => (
          <section key={tableIndex} className="mb-4">
            <h3 className="title is-6 mb-2">{table.title}</h3>
            {table.notice && <p>{table.notice}</p>}
            {table.rows.length ? (
              <div className="table-container">
                <table className="table is-fullwidth is-striped is-size-7">
                  <thead>
                    <tr>
                      {table.columns.map((column) => (
                        <th key={column.key}>{column.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {table.columns.map((column) => (
                          <td key={column.key}>
                            {formatFlowValue(row[column.key])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>{table.empty || "No rows are available."}</p>
            )}
          </section>
        ))}
        {display.attachments?.map((attachment) => (
          <ResultAttachment
            key={attachment.key}
            attachment={attachment}
            loadAttachment={loadAttachment}
          />
        ))}
      </>
    );
  }

  // Preserve dynamic, mapped custom and older RM-style results.
  const sections = response
    ? [
        {
          name: "",
          fields: Object.entries(response.fieldsMap ?? {}).map(
            ([key, label]) => ({ label: String(label), value: response[key] })
          ),
        },
      ]
    : data?.dataMap
    ? data.dataMap.map((section) => ({
        name: section.name,
        fields: Object.entries(section.data).map(([key, label]) => ({
          label,
          value: data.result[key],
        })),
      }))
    : [
        {
          name: "",
          fields: (data?.screen ?? [])
            .filter((field) => field.label)
            .map((field) => {
              const key = Object.keys(field).find(
                (key) =>
                  key.includes("selected_") || key.includes("license_plate")
              );
              return {
                label: field.label,
                value: key
                  ? data?.result[key] ?? "Not submitted"
                  : "Not submitted",
              };
            }),
        },
      ];

  if (!sections.some((section) => section.fields.length))
    return <p>No saved responses are available for this Flow yet.</p>;
  return (
    <>
      {sections.map((section, index) => (
        <section key={index} className="mb-3">
          {section.name && <h3 className="title is-6">{section.name}</h3>}
          {section.fields.map((field, fieldIndex) => (
            <div key={fieldIndex} className="mb-2">
              <strong>{field.label}:</strong>
              <br />
              {formatLegacyFlowValue(field.value)}
            </div>
          ))}
        </section>
      ))}
    </>
  );
};
